import React, { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import clearedTalentLogo from '../basicInfoForm/images/logo_recruiter_header.png';
import './candidateanalysisprint.css';
import Download from '../Icons/download';

const BRAND_URL = 'https://www.clearedtalent.com';
const CURRENT_YEAR = new Date().getFullYear();

const getStatusClass = (status) => {
  if (status === 'completed') return 'print-status-completed';
  if (status === 'initiated') return 'print-status-initiated';
  if (status === 'interrupted') return 'print-status-interrupted';
  return 'print-status-unknown';
};

const getQuestionRating = (qa) => qa?.score ?? qa?.rating ?? 'N/A';

const getPdfQuestions = (questions) => {
  if (!questions?.length) return [];
  const lastQuestion = questions[questions.length - 1];
  const lastRating = lastQuestion?.score ?? lastQuestion?.rating;
  if (lastRating === 0 || lastRating === '0') {
    return questions.slice(0, -1);
  }
  return questions;
};

const getDocumentTitle = (name) => {
  const safeName = (name || 'candidate')
    .replace(/[^\w\- ]+/g, '')
    .trim()
    .replace(/\s+/g, '-');
  return `${safeName || 'candidate'}-interview-report`;
};

export const CandidateAnalysisPrintContent = React.forwardRef(({
  analysisData,
  interviewDuration,
  interviewStatus,
  interviewStatusDisplay,
  questions = [],
  videoUrl,
  transcriptUrl,
  formatDuration,
  formatDate,
}, ref) => (
  <div ref={ref} className="candidate-analysis-print">
    <div className="print-brand-header">
      <img src={clearedTalentLogo} alt="ClearedTalent" className="print-brand-logo" />
      <a href={BRAND_URL} className="print-brand-website" target="_blank" rel="noopener noreferrer">
        www.clearedtalent.com
      </a>
    </div>

    <h1 className="print-report-title">AI Interview Assessment Report</h1>

    <div className="print-candidate-card">
      <div className="print-candidate-info">
        <p className="print-candidate-name">{analysisData?.name || 'N/A'}</p>
        <p className="print-sub-item">{analysisData?.email || 'N/A'}</p>
        <p className="print-sub-item">
          Position: {analysisData?.jobdescriptiondetails?.jobTitle || 'N/A'}
        </p>
        <p className="print-sub-item">
          Interview Date: {formatDate(analysisData?.createdAt)}
        </p>
        <p className="print-sub-item">
          Duration: {formatDuration(interviewDuration)}
        </p>
        <div className="print-status-row">
          <span className="print-status-label">Interview Status:</span>
          <span className={`print-status-badge ${getStatusClass(interviewStatus)}`}>
            {interviewStatusDisplay}
          </span>
        </div>
      </div>
      <div className="print-rating-card">
        <span className="print-rating-label">Overall Rating</span>
        <span className="print-rating-value">{analysisData?.score ?? 'N/A'}</span>
      </div>
    </div>

    <div className="print-section-card">
      <h2 className="print-section-heading">Interview Video</h2>
      <div className="print-video-section">
        <p className="print-video-note">
          Interview video is not embedded in this PDF. Click the link below to watch it in your browser.
        </p>
        {videoUrl ? (
          <a href={videoUrl} className="print-video-link" target="_blank" rel="noopener noreferrer">
            {videoUrl}
          </a>
        ) : (
          <p className="print-video-note">Video URL unavailable.</p>
        )}
      </div>
    </div>

    <div className="print-section-card">
      <h2 className="print-section-heading">Interview Transcript</h2>
      <div className="print-video-section">
        <p className="print-video-note">
          The full interview transcript is not embedded in this PDF. Click the link below to download or view it in your browser.
        </p>
        {transcriptUrl ? (
          <a href={transcriptUrl} className="print-video-link" target="_blank" rel="noopener noreferrer">
            {transcriptUrl}
          </a>
        ) : (
          <p className="print-video-note">Transcript URL unavailable.</p>
        )}
      </div>
    </div>

    <div className="print-section-card">
      <h2 className="print-section-heading">Question-by-Question Analysis</h2>
      {getPdfQuestions(questions).map((qa, index) => (
        <div key={`qa-${index}`} className="print-qa-item">
          <div className="print-qa-header-row">
            <span className="print-question-number">Q{index + 1}</span>
            <span className="print-qa-question">{qa.question}</span>
            <span className="print-question-rating">{getQuestionRating(qa)}</span>
          </div>
          <p className="print-qa-answer">
            <span className="print-qa-answer-label">Answer: </span>
            {qa.answer || 'N/A'}
          </p>
        </div>
      ))}
    </div>

    <div className="print-brand-footer">
      <p className="print-footer-text">
        ClearedTalent © {CURRENT_YEAR}. All Rights Reserved.
      </p>
      <a href={BRAND_URL} className="print-footer-link" target="_blank" rel="noopener noreferrer">
        www.clearedtalent.com
      </a>
    </div>
  </div>
));

CandidateAnalysisPrintContent.displayName = 'CandidateAnalysisPrintContent';

export const CandidateAnalysisPrintButton = ({
  analysisData,
  interviewDuration,
  interviewStatus,
  interviewStatusDisplay,
  questions,
  videoUrl,
  transcriptUrl,
  formatDuration,
  formatDate,
}) => {
  const contentRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: getDocumentTitle(analysisData?.name),
    pageStyle: '@page { size: A4; margin: 10mm; }',
    onBeforePrint: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => setIsPrinting(false),
    onPrintError: () => setIsPrinting(false),
  });

  return (
    <>
      <button
        type="button"
        className="download-pdf-btn"
        onClick={handlePrint}
        disabled={isPrinting}
      >
        <Download fill='white' className='download-pdf-icon' />
        {isPrinting ? 'Preparing...' : 'Full Report'}
      </button>
      <div className="candidate-analysis-print-wrapper" aria-hidden="true">
        <CandidateAnalysisPrintContent
          ref={contentRef}
          analysisData={analysisData}
          interviewDuration={interviewDuration}
          interviewStatus={interviewStatus}
          interviewStatusDisplay={interviewStatusDisplay}
          questions={questions}
          videoUrl={videoUrl}
          transcriptUrl={transcriptUrl}
          formatDuration={formatDuration}
          formatDate={formatDate}
        />
      </div>
    </>
  );
};
